import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Lightbulb, Loader2, Mail, Megaphone, Store, Sparkles, Clock4, Gauge } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import GlassCard from "@/components/GlassCard";
import GradientMesh from "@/components/GradientMesh";

const CHANNEL_ICONS = {
  Instagram: Megaphone,
  Email: Mail,
  "In-store": Store,
  default: Lightbulb,
};
const EFFORT_STYLES = {
  Low: "text-[color:var(--success)] border-[color:rgba(47,231,161,0.35)] bg-[color:rgba(47,231,161,0.10)]",
  Medium: "text-[color:var(--info)] border-[color:rgba(77,183,255,0.35)] bg-[color:rgba(77,183,255,0.10)]",
  High: "text-[color:var(--warning)] border-[color:rgba(255,176,32,0.35)] bg-[color:rgba(255,176,32,0.10)]",
};

const DEFAULT_GOAL = "Boost weekday foot traffic in the next 30 days";

// Module-level cache for the most recently generated batch. Survives
// SPA navigation between mounts of ``IdeasPage`` in the same tab session
// (React unmounts the component when the user routes away, but this
// module stays loaded), and is destroyed on a hard browser refresh (a
// full page reload re-executes all JS modules). This is the smallest
// safe way to satisfy:
//   - first visit / new tab → auto-fetch
//   - explicit Generate click → new fetch
//   - browser refresh → new fetch (cache reset by reload)
//   - SPA nav back to /ideas → reuse the last batch, no quota burn
let _ideasSessionCache = { ideas: [], goal: DEFAULT_GOAL };

export default function IdeasPage() {
  const [goal, setGoal] = useState(_ideasSessionCache.goal || DEFAULT_GOAL);
  const [ideas, setIdeas] = useState(_ideasSessionCache.ideas || []);
  const [loading, setLoading] = useState(false);
  const [profileMissing, setProfileMissing] = useState(false);
  const [initialLoaded, setInitialLoaded] = useState(false);

  // Guard against duplicate concurrent fetches (double-clicks, StrictMode
  // double-invocations, or a browser rapid-refresh). Using a ref keeps the
  // check synchronous so the second click bails out instantly without
  // waiting for the ``loading`` state to flip.
  const inFlightRef = useRef(false);
  // Keep a ref of the currently visible idea titles so the ``useEffect``
  // mount-fetch and the button handler always send the freshest exclusion
  // list — without adding ``ideas`` as an effect dependency (which would
  // cause an auto-refetch loop).
  const ideasRef = useRef([]);

  useEffect(() => {
    ideasRef.current = ideas;
  }, [ideas]);

  const fetchIdeas = useCallback(async (goalText, { showToast } = { showToast: true }) => {
    const trimmed = (goalText || "").trim();
    if (!trimmed) {
      if (showToast) toast.error("Add a marketing goal");
      return;
    }
    if (inFlightRef.current) {
      // Second click while a batch is still generating — silently ignore.
      return;
    }
    inFlightRef.current = true;
    setLoading(true);
    try {
      const previous_ideas = (ideasRef.current || [])
        .map((i) => (i && typeof i.title === "string" ? i.title.trim() : ""))
        .filter(Boolean);
      const res = await api.post("/ai/marketing-ideas", {
        goal: trimmed,
        previous_ideas,
      });
      const next = Array.isArray(res.data?.ideas) ? res.data.ideas.slice(0, 6) : [];
      if (next.length) {
        // Only replace the visible cards after a successful response — the
        // previous batch stays on screen while we're generating and if the
        // request fails.
        setIdeas(next);
        _ideasSessionCache = { ideas: next, goal: trimmed };
        if (showToast) toast.success("Fresh marketing ideas ready");
      } else if (showToast) {
        toast.error("No ideas returned. Try again in a moment.");
      }
    } catch (err) {
      // Keep the previous 6 ideas visible on failure.
      const detail = err?.response?.data?.detail || "Could not generate ideas";
      if (showToast) toast.error(detail);
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  }, []);

  // Load business profile + auto-fetch an initial batch on mount so that a
  // hard browser refresh always lands the user on a fresh set of 6 ideas
  // (server-side exclusion cache still ensures it differs from the last
  // batch this user saw). Skip the auto-fetch when we already have a
  // cached batch from an earlier mount in this tab session — that path is
  // hit whenever the user SPA-navigates back to /ideas from another page.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await api.get("/profile");
        if (cancelled) return;
        const hasProfile = !!p.data?.business_type;
        setProfileMissing(!hasProfile);
      } catch (_e) {
        /* ignore */
      } finally {
        if (!cancelled) setInitialLoaded(true);
      }
      if (cancelled) return;
      // If a batch is already cached in this tab session, reuse it — this
      // keeps SPA navigation (e.g. Dashboard → Marketing Ideas) free of
      // any additional AI-generation quota charge.
      if (_ideasSessionCache.ideas && _ideasSessionCache.ideas.length > 0) {
        return;
      }
      // First visit / hard refresh — fire an initial batch. We swallow the
      // toast on the auto-load so users aren't greeted with a "Fresh
      // marketing ideas ready" banner they didn't ask for. Error toasts
      // are also suppressed on mount — the empty state carries enough
      // context.
      fetchIdeas(DEFAULT_GOAL, { showToast: false });
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchIdeas]);

  const onGenerate = (e) => {
    e?.preventDefault?.();
    fetchIdeas(goal, { showToast: true });
  };

  const showEmptyState = ideas.length === 0 && !loading;

  return (
    <div className="relative">
      <GradientMesh />
      <div className="relative">
      <PageHeader
        title="Marketing Ideas"
        subtitle="Get realistic, low-budget campaigns tailored to your business."
        testidPrefix="ideas"
      />

      <GlassCard className="p-5 mb-4">
        <form onSubmit={onGenerate} className="flex flex-col sm:flex-row gap-3">
          <Input
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            placeholder="e.g. Grow Instagram by 500 followers this month"
            data-testid="ideas-goal-input"
            className="bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
          />
          <Button
            type="submit"
            disabled={loading}
            aria-busy={loading}
            className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
            data-testid="ideas-generate-button"
          >
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
            {loading ? "Brainstorming…" : "Generate ideas"}
          </Button>
        </form>
        {profileMissing ? (
          <div className="text-xs mt-3 text-[color:var(--warning)]" data-testid="empty-marketing-ideas">
            Tip: complete your business profile (type, location, tone) for sharper, more relevant ideas.
          </div>
        ) : null}
      </GlassCard>

      {showEmptyState ? (
        <div className="py-12 text-center text-sm text-[color:var(--text-3)]" data-testid="ideas-empty-state">
          <Lightbulb className="h-6 w-6 mx-auto mb-2 text-[color:var(--brand-secondary)]" />
          {initialLoaded
            ? "Your marketing playbook will appear here. Set a goal above and hit Generate."
            : "Warming up your playbook…"}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="ideas-grid">
          {ideas.map((idea, i) => {
            const Icon = CHANNEL_ICONS[idea.channel] || CHANNEL_ICONS.default;
            return (
              <motion.div
                key={`${idea.title || "idea"}-${i}`}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className="rounded-[16px] glass glass-hover p-5"
                data-testid="marketing-idea-card"
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-9 w-9 rounded-md bg-[color:rgba(27,168,159,0.10)] flex items-center justify-center text-[color:var(--brand-primary)]">
                    <Icon className="h-4 w-4" />
                  </div>
                  <span className="text-xs text-[color:var(--text-3)]">{idea.channel}</span>
                </div>
                <div className="font-display text-base font-semibold">{idea.title}</div>
                <div className="text-sm text-[color:var(--text-2)] mt-1">{idea.summary}</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className={"inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] border " + (EFFORT_STYLES[idea.effort] || EFFORT_STYLES.Medium)}>
                    <Gauge className="h-3 w-3" /> {idea.effort} effort
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-2)]">
                    <Clock4 className="h-3 w-3" /> {idea.timeline}
                  </span>
                </div>
                <div className="text-xs mt-3 text-[color:var(--text-2)]">
                  <span className="text-[color:var(--brand-primary)] font-semibold">Why it works: </span>
                  {idea.why_it_works}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
      </div>
    </div>
  );
}
